import { createApp } from 'vue';
import App from './App.vue';
import router from './router';
import { msalPlugin } from './plugins/msalPlugin';
import { msalInstance } from './azureAdAuthConfig';
import FloatingVue from 'floating-vue';
import utility from './mixins/utility';
import utilityFetch from './mixins/utilityFetch';
import utilityAuth from './mixins/utilityAuth';

// ===== CSS読み込み順序を明示的に制御 =====
// 1. 基本ライブラリ（dependency順）
import 'tabulator-tables/dist/css/tabulator_bootstrap4.min.css';

// 2. Vue関連ライブラリ（依存関係順）
import 'vue-select/dist/vue-select.css';
import 'v-calendar/dist/style.css';
import 'floating-vue/dist/style.css';

// 3. アプリケーション固有のCSS（最後）
import './styles/reset.css';
import './styles/webfont.css';
import './styles/common.scss';

const app = createApp(App);
app.config.unwrapRefs = true;

app.use(router);
app.use(msalPlugin, msalInstance);

app.use(FloatingVue, {
  themes: {
    tooltip: {
      triggers: ['hover'],
      autoHide: true,
      placement: 'bottom',
      followMouse: true,
    },
  },
});

app.mixin(utility);
app.mixin(utilityFetch);
app.mixin(utilityAuth);

// AWS WorkSpaces環境対応のCSS読み込み監視
function monitorCSSLoading() {
  let retryCount = 0;
  const maxRetries = 3;

  function checkCSS() {
    setTimeout(() => {
      const cssLinks = document.querySelectorAll('link[rel="stylesheet"]');
      const problematicFiles = [];
      let allLoaded = true;

      cssLinks.forEach((link) => {
        try {
          // CSSが読み込まれているかより確実にチェック
          if (!link.sheet || link.sheet.cssRules.length === 0) {
            problematicFiles.push(link.href);
            allLoaded = false;
          }
        } catch (e) {
          // CORS エラーなどでアクセスできない場合も読み込み失敗とみなす
          if (e.name === 'SecurityError') {
            // 外部ドメインのCSSの場合はアクセス権限がないため正常
            return;
          }
          problematicFiles.push(link.href);
          allLoaded = false;
        }
      });

      if (!allLoaded && retryCount < maxRetries) {
        console.warn(`CSS読み込み失敗を検出 (${retryCount + 1}/${maxRetries}):`, problematicFiles);

        // 特定のファイルのみ再読み込み
        problematicFiles.forEach((href) => {
          if (href.includes('app.') || href.includes('vendor.') || href.includes('styles.')) {
            const oldLink = document.querySelector(`link[href="${href}"]`);
            if (oldLink) {
              const newLink = document.createElement('link');
              newLink.rel = 'stylesheet';
              newLink.href = href + '?retry=' + Date.now();
              newLink.onload = () => console.log('CSS再読み込み成功:', href);
              newLink.onerror = () => console.error('CSS再読み込み失敗:', href);

              // 古いリンクの後に新しいリンクを挿入
              oldLink.parentNode.insertBefore(newLink, oldLink.nextSibling);
              oldLink.remove();
            }
          }
        });

        retryCount++;
        // 再帰的にチェック
        checkCSS();
      } else if (allLoaded) {
        console.log('全てのCSSが正常に読み込まれました');
      } else {
        alert('CSS読み込みの修復に失敗しました。手動でページを再読み込みしてください。');
      }
    }, 2000 + retryCount * 1000); // 初回2秒、以降1秒ずつ延長
  }

  checkCSS();
}

// DOM読み込み完了後に監視開始
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', monitorCSSLoading);
} else {
  // すでにDOMが読み込まれている場合
  monitorCSSLoading();
}

router.isReady().then(() => {
  app.mount('#app');
});
